import { useTranslation } from 'react-i18next';
import { RedoIcon, TrashIcon, UndoIcon } from '@/utils/icons';
import { IS_MAC } from './isMac';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import styles from './Toolbar.module.css';

interface IHistoryControlsProps {
  canUndo: boolean;
  canRedo: boolean;
  canClearPage: boolean;
  disabled: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClearPage: () => void;
}

/** The history cluster: undo, redo, and clear-page (itself undoable), with
 *  platform-appropriate shortcut hints in the tooltips. */
export function HistoryControls({
  canUndo, canRedo, canClearPage, disabled, onUndo, onRedo, onClearPage,
}: IHistoryControlsProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.toolGroup}>
      <Tooltip content={`${t('documents.markup.undo')} (${IS_MAC ? '⌘Z' : 'Ctrl+Z'})`}>
        <button
          type="button"
          className={styles.toolBtn}
          onClick={onUndo}
          disabled={disabled || !canUndo}
          aria-label={t('documents.markup.undo')}
          aria-keyshortcuts="Control+Z Meta+Z"
        >
          <UndoIcon size={15} />
        </button>
      </Tooltip>
      <Tooltip content={`${t('documents.markup.redo')} (${IS_MAC ? '⇧⌘Z' : 'Ctrl+Y'})`}>
        <button
          type="button"
          className={styles.toolBtn}
          onClick={onRedo}
          disabled={disabled || !canRedo}
          aria-label={t('documents.markup.redo')}
          aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z Control+Y"
        >
          <RedoIcon size={15} />
        </button>
      </Tooltip>
      <Tooltip content={`${t('documents.markup.clearPage')} (${IS_MAC ? '⌘⌫' : 'Ctrl+Backspace'})`}>
        <button
          type="button"
          className={styles.toolBtn}
          onClick={onClearPage}
          disabled={disabled || !canClearPage}
          aria-label={t('documents.markup.clearPage')}
          aria-keyshortcuts="Control+Backspace Meta+Backspace"
        >
          <TrashIcon size={15} />
        </button>
      </Tooltip>
    </div>
  );
}